import { Component, forwardRef, Inject, OnInit, ViewChild } from '@angular/core';
import { ThemeService } from '../service/theme.service';
import { TranslateService } from '@ngx-translate/core';
import { LocalStorageService } from '../local-storage.service';
import { SystemMessageService } from '../service/system-message.service';
import { CognitoService } from '../service/cognito.service';
import { TruckService } from '../service/truck.service';
import { RouteService, Stop } from '../service/route.service';
import { ValidationService } from '../service/validation.service';
import * as L from 'leaflet';
import 'leaflet-geometryutil';
import 'leaflet-routing-machine';
import { CustomORSRouter } from '../libs/leaflet-routing-machine/custom-ors-router';
import { IInstruction, IRoute } from '../libs/leaflet-routing-machine/lrm-types';
import { RouteStopErrors } from '../constants/route-stop-errors';
import { firstValueFrom } from 'rxjs';
import { WakeLockService } from '../service/wake-lock.service';
import { CustomMultiSelectComponent } from "../custom-multi-select/custom-multi-select.component";
import { environment } from "../environments/environment";

@Component({
    selector: 'app-driver-routes',
    templateUrl: './driver-routes.component.html',
    styleUrls: ['./driver-routes.component.css'],
    standalone: false
})
export class DriverRoutesComponent implements OnInit{
  constructor(
    @Inject(forwardRef(() => TranslateService)) private translate: TranslateService,
    public theme: ThemeService,
    public localStorageService: LocalStorageService,
    public systemMessageService: SystemMessageService,
    public cognitoService: CognitoService,
    public truckService: TruckService,
    public routeService: RouteService,
    public validationService: ValidationService,
    public wakeLockService: WakeLockService
  ){}

  @ViewChild('stopSelect', { static: false }) private stopSelect!: CustomMultiSelectComponent;

  public language: string = this.localStorageService.getItem('language');
  public displayRoutesList: boolean = true;
  public displayStopsAndMap: boolean = false;
  public waitingForLocation: boolean = false;

  private route_id: string = '';
  private driver_id: string = '';
  private truck_id: string = '';

  private map!: L.Map;
  private routingControl: any;
  private userMarker!: L.Marker; // User marker
  public selectedStopDetails?: Stop;
  public selectedError: string = '';
  public cantPickupTextDetail: string = '';

  private route?: IRoute = undefined;
  private turnMarkers: L.Marker[] = [];

  private navigationStarted: boolean = false;
  private startNavControl: L.Control = new L.Control({ position: 'topright' });
  // private recomputeRouteControl: L.Control = new L.Control({ position: 'bottomleft' });
  private instructionIndex: number = 0;                         // Index of the current instruction/segment
  private speedsSinceLastInstruction : { speed: number, timestamp: number }[] = [];           // Array of all the speeds (with their respective timestamps) since last instruction/segment
  private lastPosition?: L.LatLng = undefined;
  private distanceLeftToNextSegment: number = Number.MAX_VALUE;
  private readonly reachesSoonTimeThreshold: number = 15;       // Time in seconds to reach location
  private readonly instructionDistanceThreshold: number = 15;   // Distance in meters to read next instruction
  private readonly binProximityThreshold: number = 40;          // Distance in meters to be around a bin to accept it
  private readonly positionUpdateThreshold: number = 0.5;        // Positions < 10 meters away from last position will be ignored

  private voices: SpeechSynthesisVoice[] = [];
  private langToUseWithVoice: string = '';
  private isSoundMuted: boolean = false;
  private pickupModal: any;
  private cantPickupModal: any;
  public RouteStopErrors = RouteStopErrors;
  public selectedStopIndex: number | undefined = undefined;     // Value is set in frontend

  private readonly mapAutoFocusTimeThreshold: number = 3000; // 5 Sec before the view resets after the user interacted with the map
  private resetInteractionTimeoutId: number = -1;
  private userInteractedWithMap: boolean = false;
  private readonly defaultZoomLevel: number = 16.5;
  private currentZoomLevel: number = 15;

  private readonly offCourseThreshold: number = 15; //15m away from the 'line' would be considered as "off-course" by the algorithm
  private wasOffCourse: boolean = false;              // Flag to indicate if we have diverged from the computed route
  private isZoomLocked: boolean = false;              // Flag to indicate that the map is doing a zoom animation

  private isStopSelectDropdownVisible: boolean = false;

  get stopOptions() {
    return this.routeService.stopsArray.map((stop) => ({
      label: `Stop ${stop.index}`,
      value: stop.index,
    }));
  }

  async ngOnInit(){
    await this.cognitoService.getUser(); // TODO this is already called in the service, find a way to wait for it
    this.routeService.driver_id = this.cognitoService.userInfos.attributes['sub'];
    this.driver_id = this.routeService.driver_id;
    // Get truck associated with the driver
    this.truck_id = await this.truckService.getTruckIdByDriverId(this.driver_id);
    // Get the routes associated with our driver
    await this.routeService.getRoutesIdByTruckId(this.truck_id);
    this.pickupModal = document.getElementById('pickup-modal');
    this.cantPickupModal = document.getElementById('cant-pickup-modal');
    // Do we have routes?
    if (this.routeService.driverRoutesArray.length <= 0) {
      alert('NO ROUTES AVAILABLE');
    }
    this.setLanguageToUseForTTS()
    this.loadVoices();
    window.speechSynthesis.onvoiceschanged = () => {
      this.loadVoices();
    };
    // TODO add loading screen on init
  }

  loadVoices() {
    this.voices = window.speechSynthesis.getVoices();
  }

  /**
   * Initialize the map in the map-container HTML element, also position the user marker and all the stop markers.
   * Then we check every couples of seconds if we are close to a marker
  */
  private mapInit(){
    // Set the map on the HTML element and set initial view (necessary for map tiles to load) at Muirwood Studio location.
    this.map = L.map('map-container', {
      zoomSnap : 0.01,//0.25,
      maxZoom: 18, //Limit how much users can zoom in to reduce the load on the tile-server
    });//.setView([45.650, -73.848], 16, { noMoveStart: true });

    // Get the map tiles and add it to the map
    L.tileLayer(environment.tileServer + '/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
    }).addTo(this.map);

    // Define the button that will start the navigation (only shown once a route is found using LRM)
    this.startNavControl.onAdd = (map) => {
      let div = L.DomUtil.create('div', 'leaflet-bar leaflet-control leaflet-control-custom');
      div.innerHTML = `<button id="startNavBtn" class="${this.theme.getThemeClass()}" style="padding: 5px;">${this.translate.instant('StartNav')}</button>`;

      // Prevent map interaction when clicking the button
      L.DomEvent.disableClickPropagation(div);

      const button = div.querySelector('#startNavBtn');
      if (button) {
        button.addEventListener('click', (e) => {
          this.startNavigationHandler();
          e.stopImmediatePropagation();
          e.stopPropagation();
        }, {
          once: true,
          passive: true,
          capture: true,
        });
      }

      return div;
    };

    //! Never got that part working, running out of time, putting on back-burner
    // Define the button that will start the navigation (only shown once a route is found using LRM)
    /*this.recomputeRouteControl.onAdd = (map) => {
      let div = L.DomUtil.create('div', 'leaflet-bar leaflet-control leaflet-control-custom');
      div.innerHTML = `<button id="recomputeRouteBtn" class="${this.theme.getThemeClass()}" style="padding: 5px;">kokokokokoko${this.translate.instant('RecomputeRoute')}</button>`;

      // Prevent map interaction when clicking the button
      L.DomEvent.disableClickPropagation(div);

      const button = div.querySelector('#recomputeRouteBtn');
      if (button) {
        button.addEventListener('click', (e) => {
          debugger;
          alert("ca marche!");
          e.stopImmediatePropagation();
          e.stopPropagation();
        }, {
          once: true,
          passive: true,
          capture: true,
        });
      }

      return div;
    };*/

    // Make Leaflet keep track of the user's position on the map
    this.map.locate({
      watch: true,              // Constantly fetch user's position
      enableHighAccuracy: true,
      maximumAge: 0,            // Never use cached position, always fetch new
      timeout: 15000,           // 15s timeout
    });

    // This event handler will run once, on the first GPS event
    this.map.once('locationfound', (event) => {
      // Location found, we can now display the map section
      this.waitingForLocation = false;

      // Force Leaflet to redraw itself on next browser paint
      // Fixes tiles that might not have been loaded because the map was not visible
      requestAnimationFrame(() => {
        this.map.invalidateSize();
        // Focus the map on the position
        this.map.setView(this.userMarker.getLatLng(), this.defaultZoomLevel, { noMoveStart: true, animate: true, easeLinearity: 0.25, duration: 5 });
      });

      // Creates the user's position marker on the map, if needed
      if(!this.userMarker || !this.map.hasLayer(this.userMarker)){
        this.createPositionMarker(event);
      }

      // Now bind the actual event handler for GPS updates
      this.map.on("locationfound", this.onLocationFoundHandler.bind(this));
    });

    this.map.on('locationerror', this.GeolocationPositionErrorHandling);

    this.registerUserMapInteractionHandlers();

    // Prevent mobiles screens from going to sleep
    this.wakeLockService.requestWakeLock();
  }

  /**
   * Check if the user marker is close to a stop marker
   * @param stopIndex
   * @private
   */
  private isInProximityAndInProgress(stopIndex: any): boolean {
    // Check if userMarker is defined
    if(!this.userMarker){
      console.log('User marker is not defined');
      return false;
    }
    // Get our user latitude and longitude
    const userLatLng = this.userMarker.getLatLng();
    // Get the selected stop
    const stop = this.routeService.stopsArray[stopIndex-1];
    // Format our gps coordinate into an array of number
    const latLng: number[] = stop.bin_gps.split(',').map(Number);
    // Make our array of gps coordinate into a LatLngTuple for distance comparison
    const stopLatLng: L.LatLngTuple = [latLng[0],latLng[1]];
    // Set distance between the user and the stop
    const distance = userLatLng.distanceTo(stopLatLng);
    // Check if we are within range
    if(distance < this.binProximityThreshold){
      // Check if the stop is in progress
      if(stop.status == 'I'){
        return true
      }
      else{
        alert('This stop has already been completed');
        return false;
      }
    }
    else{
      alert('You are not close enough to the bin, you need to be within ' + this.binProximityThreshold + ' meters.');
      return false
    }
  }

  // Click on route and open map with routes stops
  async clickOnRoute(route_id: string){
    const selectedRoute = this.routeService.driverRoutesArray.find((route: any) => route.route_id == route_id);
    // If the route has no start_date, the route hasn't started so display start route message
    if(selectedRoute.start_date == '' || selectedRoute.start_date == null){
      const message = await firstValueFrom(this.translate.get('startNewRoute'));
      if(confirm(message)){
        await this.setupStopsPage(route_id);
      }
    }
    // If we have a end_date, the route has already been complete
    else if (selectedRoute.end_date){
      const message = await firstValueFrom(this.translate.get('routeCompleted'));
      alert(message)
      return
    }
    // If the route is already started but not completed
    else{
      await this.setupStopsPage(route_id);
    }
  }

  private async setupStopsPage(route_id: string) {
    this.route_id = route_id;
    this.switchHTML();
    // Set the start date of the route
    /*await*/ this.routeService.startRoute(route_id);
    // Fill the route array with the result of the query
    this.routeService.stopsArray = await this.routeService.getStopsDetails(route_id);

    // TODO add loading screen on click
    //await this.sleep(50);
    // Set CSS of labels
    this.setInitialStopLabelsCssClass();

    // Hide routes list
    this.displayRoutesList = false;
    // Initialize map
    this.mapInit();
  }

  /**
   * Called whenever the stop selector (ng-select) is clicked
   * <br>
   * For the first click (on mobile), prevents the virtual keyboard opening.
   * <br>
   * For other devices, or for subsequent clicks, enables and focuses on the input field
   * @param clickEvent
   */
  public onStopSelectClicked(clickEvent: any) {
    const input: HTMLInputElement = this.stopSelect.searchInput?.nativeElement as HTMLInputElement;
    if(!input)
      return;

    // Case 1 : non-mobile device, so we *want* to focus on the input
    // Case 2 : second touch event from mobile device
    if(this.isStopSelectDropdownVisible || clickEvent.pointerType !== "touch"){
      input.disabled = false;
      input.focus();
      return;
    }

    // Remember that the dropdown is open, disable the input (only reached on mobile devices)
    this.isStopSelectDropdownVisible = this.stopSelect.isOpen ?? false;
    input.disabled = true;
  }

  /**
   * Called when the stop select dropdown is closed
   */
  public onStopSelectClose() {
    this.isStopSelectDropdownVisible = false;
  }

  setInitialStopLabelsCssClass() {
    const elements = document.querySelectorAll<HTMLOptionElement>('.stop-label');
    const stops = this.routeService.stopsArray;

    Array.from(elements).forEach((element, index) => {
      switch(stops[index].status){
        case 'I': // In progress
          break;
        case 'C': // Completed
          element.classList.add('stop-label-success', 'disabled');
          element.disabled = true
          break;
        case 'X': // Canceled
          element.classList.add('stop-label-error');
          element.disabled = true;
          element.setAttribute('ng-disabled', 'true');
          element.style.pointerEvents = 'none';
          break;
        case 'D': // Delayed
          break;
        case 'P': // Pending
          element.classList.add('stop-label-error');
          element.disabled = true;
          element.setAttribute('ng-disabled', 'true');
          element.style.pointerEvents = 'none';
          break;
        default:
          break;
      }
    });
  }

  clickOnStop(stopIndex: number){
    if(!stopIndex)
      return;
    // If our stop is completed, exit and do nothing
    const isComplete = this.routeService.stopsArray[stopIndex-1].status == 'C';
    if(isComplete){
      return;
    }
    // Stop the TTS if it's playing
    this.stopVoice();

    this.selectedStopHandler(stopIndex);
  }

  /**
   * The function that handle the function to call and the variables to set when we click on a stop
   * @param stopIndex
   * @private
   */
  private selectedStopHandler(stopIndex: number) {
    // Set stop detail
    this.selectedStopDetails = this.routeService.stopsArray[stopIndex - 1];
    // Extract it's latitude and longitude
    const [latitude, longitude] = this.selectedStopDetails.bin_gps.split(',').map(Number);
    // Close pickup modal, in case its open
    this.closePickupModal();
    // Create route
    this.createStopRouting(new L.LatLng(latitude, longitude));
  }

  switchHTML(){
    this.displayRoutesList = !this.displayRoutesList;
    this.displayStopsAndMap = !this.displayStopsAndMap;

    // We no longer wait for GPS location at this point, so we have to hide the loading animation
      this.waitingForLocation = this.displayStopsAndMap;
  }

  /**
   * Create a route from our user position to a single destination and display it on the map
   * @param destination
   */
  private createStopRouting(destination: L.LatLng){
    // Initial position and last position
    const initialPosition = this.userMarker.getLatLng();
    this.lastPosition = this.userMarker.getLatLng();

    // If we already have a route on map remove it
    if(this.routingControl){
      this.map.removeControl(this.routingControl);
    }

    // Define the waypoints for the start and finish locations
    const waypoints = [initialPosition, destination];

    // Custom start/end icons for the waypoints' markers
    const startIcon = this.createStandardMarkerIcon('geo-alt-fill', '#13a928', 'fs-3');
    const endIcon = this.createStandardMarkerIcon('trash-fill', '#D00', 'fs-3');

    // Create a route from our user to our destination
    this.routingControl = L.Routing.control({
      waypoints: waypoints,
      // Override to prevent dragging of waypoints by the user. See https://stackoverflow.com/a/39123518 and https://stackoverflow.com/a/37107794
      plan: L.Routing.plan(waypoints, {
        createMarker: (i, wp) => {
          return L.marker(wp.latLng, {
            draggable: false,
            icon: i == 0 ? startIcon : endIcon,
          });
        }
      }),
      addWaypoints: false,
      routeWhileDragging: false,
      router: new CustomORSRouter(this.language),
      fitSelectedRoutes: false, // we will use flyToBounds for a smoother transition
    }).addTo(this.map);

    // Set our route instruction
    this.routingControl.on('routesfound', this.routeFoundHandler.bind(this));

    // Add route location handler
    //this.map.on('locationfound', this.onLocationFoundHandler.bind(this));
  };

  /**
   * TODO add proper docstring
   * @param event
   * @private
   */
  private onLocationFoundHandler(event: L.LocationEvent): void {
    // Location has been found
    this.waitingForLocation = false;

    const newLocation = event.latlng;
    this.userMarker.setLatLng(newLocation); // Update user marker position

    // ignore noise in geolocation (only really needed when not moving)
    if (this.lastPosition && !this.hasPositionChanged(this.lastPosition, newLocation))
      return;

    // Update map's view/zoom if user didn't manually move the map
    // Only update positions if no route or after the route has been started (otherwise we would override the flyToBounds on the route's bbox)
    if (!this.userInteractedWithMap && (!this.route || this.route && this.navigationStarted)){
      // Set zoom level, it will be applied on the next GPS event
      this.currentZoomLevel = this.getUpdatedZoomLevel(event.speed, this.distanceLeftToNextSegment ?? Number.MAX_VALUE);
      this.moveMapSmoothly(newLocation, this.currentZoomLevel);
    }

    // No need to check for progression if the route is not yet started!
    if (this.navigationStarted && this.route) {
      this.handleSegmentProgression(event);

      if (typeof this.route.waypoints[1] === 'number') {
        // It's a number: use it as an index into coordinates
        this.checkIfAtDestination(this.route.coordinates[this.route.waypoints[1]]);
      } else {
        // It's an L.LatLng: use it directly
        this.checkIfAtDestination(this.route.waypoints[1]);
      }
    }

    // Setup for next event
    this.lastPosition = this.userMarker.getLatLng();
  }

  /**
   * Creates the user's position marker on the map and focuses on it
   * <b>this.map must already be set</b>
   * @param event {L.LocationEvent} The location event from leaflet
   * @private
   */
  private createPositionMarker(event: L.LocationEvent){
    // Custom marker for user's position
    const userIcon = new L.DivIcon({
      className: 'user-position-marker', // defined in CSS file
    });

    // Create a user marker at the L.LocationEvent's value, using our custom icon
    this.userMarker = L.marker(event.latlng, { icon: userIcon });

    // Add marker to the map
    this.map.addLayer(this.userMarker);
    console.log("%cAdding user's marker to the map.", "color:goldenrod;");
  }




//region detour algo stuff
  /**
   * Checks if the given position is off-course on the current route
   * @param position The position to check
   * @return {boolean} True if off course, false if on course
   * @private
   */
  private checkIsOffCourse(position: L.LatLng): boolean{
    if(!this.route)
      return false;

    // Helps prevent false-positive detections by making it easier to be considered off-course when already off-course
    const offset = this.wasOffCourse ? 10 : 0;
    let closestPoint = L.GeometryUtil.closest(this.map, this.route.coordinates, position, false);
    return closestPoint ? new L.LatLng(closestPoint.lat, closestPoint.lng)?.distanceTo(position) >= (this.offCourseThreshold - offset) : false;
  }

  /**
   * Finds the closest forward-facing turn point from current location, assuming we are on the route.
   * @param currentLocation Current user location
   * @param route The IRoute object to search
   * @returns {number | null} Closest forward-facing instruction/turn index or undefined
   */
  private getForwardClosestTurnPointIndex(currentLocation: L.LatLng, route: IRoute): number | null {
    //&TODO Figure out if we can skip this step somehow to make it faster
    // Snap to the route's polyline
    let snappedPoint: L.LatLng = L.GeometryUtil.closest(this.map, route.coordinates, currentLocation, false) as any; // as any because the types seem to be wrongly defined in the lib...

    // Find the index of the point on the route's polyline
    let snappedIndex: number = this.findClosestCoordinateIndex(snappedPoint, route.coordinates);

    //&TODO Pretty sure the first one we come across would be the one we want 99.9% of the time no? Maybe we can avoid the for loop...
    // Search through all the following turnPoints to find the next one
    let bestMatch: number | null = null;
    let bestDistance: number = Number.MAX_VALUE;

    for (let i = 0; i < route.turnPoints.length; i++) {
      //&TODO probably overkill/unnecessary since the coords should already match? TO BE TESTED
      let turnPoint = route.turnPoints[i];
      let turnCoordsIndex = this.findClosestCoordinateIndex(turnPoint, route.coordinates);

      console.log(`i:${i}\nturnCoordsIndex:${turnCoordsIndex}`);

      if (turnCoordsIndex >= snappedIndex) {
        let distance = currentLocation.distanceTo(turnPoint);
        if(distance < this.instructionDistanceThreshold)
          continue;

        if (distance < bestDistance) {
          bestDistance = distance;
          bestMatch = i;
        }
      }
    }

    return bestMatch;
  }

  /**
   * Finds the index of the closest coordinate to given target point
   * @param target Point to compare
   * @param coords Array of points to search
   * @returns Index of closest point
   */
  private findClosestCoordinateIndex(target: L.LatLng, coords: L.LatLng[]): number {
    let bestIndex: number = 0;
    let minDistance: number = Number.MAX_VALUE;

    for (let i = 0; i < coords.length; i++) {
      let d = coords[i].distanceTo(target);
      if (d < minDistance) {
        minDistance = d;
        bestIndex = i;
      }
    }

    return bestIndex;
  }
//endregion detour algo stuff



  /**
   * Computes the appropriate zoom level for the leaflet map, given a speed and a distanceLeft values
   * <br>
   * Slower speeds and shorter distances will result in a higher zoom level (max: 18, min: 15.5)
   * @param speed
   * @param distanceLeft
   * @return {number} The calculated zoom level
   * @private
   */
  private getUpdatedZoomLevel(speed: number, distanceLeft: number): number {
    // If debounce is active, skip this zoom update
    // if (this.isZoomLocked || this.userInteractedWithMap) {
    //   return;
    // }

    const currentZoom: number = this.map.getZoom();
    let targetZoom: number;

    // Up to 39.6 km/h
    if (speed <= 11)        targetZoom = 18.00;
    // Up to 61.2 km/h
    else if (speed <= 17)   targetZoom = 17.50;
    // Up to 86.4 km/h
    else if (speed <= 24)   targetZoom = distanceLeft > 200 ? 16.50 : 17.00;
    // Up to 108.0 km/h
    else if (speed <= 30)   targetZoom = distanceLeft > 500 ? 16.00 : 16.50;
    // Anything faster
    else                    targetZoom = distanceLeft > 1000 ? 15.50 : 16.00;

    // Skip if no meaningful change in zoom level
    if (Math.abs(targetZoom - currentZoom) <= 0.20)
      return this.currentZoomLevel;

    return targetZoom;

    // Prevent further zoom update by locking the zoom for 2s
    //this.isZoomLocked = true;
    //window.setTimeout(() => { this.isZoomLocked = false }, 200);
  }

  /**
   * Main function that handles the live GPS routing
   * <br>
   * This function checks the current location, speed, distance left, etc. to figure out the progression along the current route
   * <br>
   * Uses TTS to speak the different instructions at the appropriate time
   * @param locationEvent
   * @private
   */
  private handleSegmentProgression(locationEvent: L.LocationEvent) {
    // Return immediately if route is not set or if we already completed all instructions
    if (!this.route || this.instructionIndex >= this.route.instructions.length) {
      console.log("!this.route : ", !this.route);
      console.log("this.instructionIndex >= this.route.instructions.length", this.instructionIndex >= (this.route?.instructions.length ?? -1));
      alert("Not supposed to be triggered");
      //TODO Managed to trigger this once, unable to replicate since...
      return;
    }

    /** Array that contains all the texts to 'speak' before the function returns */
    let textsToTTS: string[] = [];

    /** Flag used to keep track of the on-course status when 'this.wasOffCourse' is reset to false */
    let justGotBackOnCourse = false;

    // Detours handling
    if(this.checkIsOffCourse(locationEvent.latlng)){
      this.handleDetour();
      // No need to compute anything else since the user is not currently on course anyway
      return;
    }
    // We are no longer off course but we were during last GPS event, meaning we are back *on* course!
    else if(this.wasOffCourse){
      this.recoverFromDetour(locationEvent);
      justGotBackOnCourse = true;
      console.log("%cBACK ON COURSE", "color: #eee; background: #333; padding: 4px 8px; border-radius: 4px;");
    }

    /** Current instruction */
    const currentInstruction = this.route.instructions[this.instructionIndex];
    /** Distance in meters between the last turn and the current turn */
    const currentSegmentLength = currentInstruction.distanceFromLastInstruction;
    /** Coordinates of the incoming turn, leading to next segment/instruction */
    const currentTurnCoords = currentInstruction.segmentStartCoordinates;
    /** Distance left to reach current instruction */
    const currentDistanceLeft = locationEvent.latlng.distanceTo(currentTurnCoords);
    /** Rounded distance left to reach current instruction */
    const currentRoundedDistanceLeft = DriverRoutesComponent.roundValueBasedOnDistance(currentDistanceLeft);
    // Update distance left for zoom computation in next event
    this.distanceLeftToNextSegment = currentRoundedDistanceLeft;

    // Compute average speed up to current point
    this.speedsSinceLastInstruction.push({ speed: locationEvent.speed, timestamp: locationEvent.timestamp });
    /** Average speed over the last 5 seconds */
    const avgSpeed = this.recentAvgSpeed(5);

    /** Predicted distance, in the following 3 seconds */
    let predictedDistance = avgSpeed * 3;

    // Did we reach the instruction's distance threshold (adjusted based on our avgSpeed)?
    if (currentDistanceLeft - predictedDistance <= this.instructionDistanceThreshold) {
      currentInstruction.wasReadFromNear = true;
      textsToTTS.push(currentInstruction.text);

      // If we are here, we reached the instruction, we can increment the index to go to the following instruction
      this.instructionIndex++;
      // Clear the speeds array
      this.speedsSinceLastInstruction = [];

      // Check if there is an instruction at new index or if we reached the end of the instructions array (aka the destination)
      if(this.instructionIndex < this.route.instructions.length){
        // Fetch following instruction
        const nextInstruction = this.route.instructions[this.instructionIndex];
        // Coordinates of the turn at the end of the next segment/instruction. We take the arrival coords as a fallback in case nextInstruction is null
        const nextTurnCoords = nextInstruction?.segmentStartCoordinates ?? this.route.coordinates[this.route.coordinates.length - 1];

        // Check how much distance left to reach next instruction
        const nextDistanceLeft = locationEvent.latlng.distanceTo(nextTurnCoords);
        const nextRoundedDistanceLeft = DriverRoutesComponent.roundValueBasedOnDistance(nextDistanceLeft);
        this.distanceLeftToNextSegment = nextRoundedDistanceLeft;

        // Read next instruction with distance to give a rough idea of the route to follow to the driver
        textsToTTS.push(this.getLocalizedDistanceLeftText(nextRoundedDistanceLeft) + nextInstruction.text);
      }
    }
    else{
      // We did not reach the currentTurnCoords
      // We need to test whether we will reach soon or not

      // Special case if we just got back on course
      if(justGotBackOnCourse){
        //if(!currentInstruction.wasReadFromAfar)
        {
          console.log("%cBACK ON COURSE INSTRUCTION CALL", "color:RED;background:black;padding:2px;border-radius:5px");
          // We can safely read the instruction with it's remaining distance to give a rough idea how much is left before next turn
          textsToTTS.push(this.getLocalizedDistanceLeftText(currentRoundedDistanceLeft) + currentInstruction.text);
        }
      }

      // Ignore if prediction call was already made
      if(!currentInstruction.wasReadFromNear){
        let predictionDistance = this.getPredictionDistance(currentSegmentLength, avgSpeed);

        // Check if we will reach soon
        if (currentDistanceLeft <= predictionDistance){
          // If we maintain this speed average, we will reach soon

          // Make sure the instruction's total distance was more than predictionDistance and is more than at least 500m
          // Instructions with distance of less than 500m won't get a prediction TTS
          if(predictionDistance < currentInstruction.distanceFromLastInstruction && currentInstruction.distanceFromLastInstruction >= 500){
            console.log("PREDICTION BASED TTS", "color: #eee; background: #333; padding: 4px 8px; border-radius: 4px;");
            // We can safely read the instruction with it's remaining distance to give a rough idea how much is left before next turn
            textsToTTS.push(this.getLocalizedDistanceLeftText(currentRoundedDistanceLeft) + currentInstruction.text);
            // Prevent prediction-reading it a seconde time
            currentInstruction.wasReadFromNear = true;
          }
        }
      }
    }

    // Read all accumulated texts
    this.readTexts(textsToTTS);
  }

  /**
   *
   * @private
   */
  private handleDetour(): void {
    //! We are currently off-course

    //&TODO Inform user?
    //&TODO Button to re-compute the route?
    //&TODO Trigger Re-route popup/button/alert/other

    // Make sure to only add it once
    if (!this.wasOffCourse) {
      setTimeout(() => {
        /*this.recomputeRouteControl.addTo(this.map);
        document.getElementById('recomputeRouteBtn')?.addEventListener('click', () => {
          //!TODO Actually recompute the route
          this.recomputeRouteControl.remove();
        }, { once: true });*/
      }, 2000);// 2s delay to avoid the button popping up for very small deviations from the route or accuracy fluctuations from the GPS
    }

    // We need to remember that we are off-course
    this.wasOffCourse = true;
  }

  /**
   *
   * @param locationEvent
   * @private
   */
  private recoverFromDetour(locationEvent: L.LocationEvent): void {
    // We are back on course
    this.wasOffCourse = false;

    let forwardTargetIndex = this.getForwardClosestTurnPointIndex(locationEvent.latlng, this.route!);

    if (forwardTargetIndex) {
      this.instructionIndex = forwardTargetIndex;
      console.log("%cRedirecting to instruction:", "color: #eee; background: #333; padding: 4px 8px; border-radius: 4px;", this.instructionIndex, this.route!.instructions[this.instructionIndex]);
    }
  }

  /**
   * Returns the rounded distance value based on the given distance. Greater distances are more aggressively rounded.
   *
   * Scales in natural increments :
   * - 50km or more ------> round to 1000m (1km)
   * - 1km  or more ------> round to 100m (0.1km)
   * - 500m or more ------> round to  50m
   * - 100m or more ------> round to  10m
   * - Anything shorter --> round to   5m
   *
   * @param {number} distance - The distance in meters.
   * @returns {number} The rounded value in meters.
   * @private
   */
  private static roundValueBasedOnDistance(distance: number): number{
    let roundingFactor: number;
    switch (true) {
      case distance >= 50000: roundingFactor = 1000; break;
      case distance >=  1000: roundingFactor =  100; break;
      case distance >=   500: roundingFactor =   50; break;
      case distance >=   100: roundingFactor =   10; break;
      default:                roundingFactor =    5; break;
    }

    return Math.floor(distance / roundingFactor) * roundingFactor;
  }

  //region Predictions
  /**
   * Returns the distance value to use when making predictions.
   *
   * Internally uses both predictDistanceFromSegmentDistance and predictDistanceFromSpeed
   * and returns the average, if it's smaller than 3/4 of the segmentDistance. If not, returns the value from predictDistanceFromSegmentDistance
   * @param {number} segmentDistance - The segment's total distance in meters
   * @param {number} speed - The speed in meters per second
   * @param {number} time - The time in seconds
   * @returns {number} The prediction distance in meters
   * @private
   * @see DriverRoutesComponent.predictDistanceFromSegmentDistance
   * @see DriverRoutesComponent.predictDistanceFromSpeed
   */
  private getPredictionDistance(segmentDistance: number, speed: number, time: number = this.reachesSoonTimeThreshold){
    const distanceBasedPrediction = this.predictDistanceFromSegmentDistance(segmentDistance);
    const speedBasedPrediction = this.predictDistanceFromSpeed(speed, time);

    let average = (distanceBasedPrediction + speedBasedPrediction) / 2;

    // Return the average only if its smaller than 3/4 of the totalDistance
    return average < (segmentDistance * 3/4) ? average : distanceBasedPrediction;
  }

  /**
   * Returns the distance value to use when making predictions.
   *
   * The faster the speed, the greater the prediction distance.
   * This will give drivers more time to adjust (eg.: change lanes) before they reach the next instruction.
   *
   * Rough formula is y = (x^2) where x is the speed and y is the distance (assuming default prediction time of 15s)
   *
   * Scales in the following way :
   * - 29m/s (~104km/h) or more --> 900m
   * - 24m/s (~86km/h)  or more --> 625m
   * - 18m/s (~65km/h)  or more --> 400m
   * - 12m/s (~43km/h)  or more --> 225m
   * - 6m/s (~22km/h)   or more --> 100m
   * - Anything slower          -->  25m
   *
   * @param {number} speed - The speed in meters per second.
   * @param {number} time - The time in seconds
   * @returns {number} The prediction distance in meters.
   * @private
   */
  private predictDistanceFromSpeed(speed: number, time: number = this.reachesSoonTimeThreshold): number{
    return speed * time;
  }

  /**
   * Returns the distance value to use when making predictions.
   *
   * The greater the distance, the greater the prediction distance (capped at 750m for distances of 2500m or more).
   * This will give drivers more time to adjust (eg.: change lanes) before they reach the next turn/instruction.
   *
   * Scales in the following way :
   * - 2500m or more    --> 750m
   * - 1000m or more    --> 400m
   * - 500m  or more    --> 225m
   * - Anything shorter -->  25m
   *
   * @param {number} segmentDistance - The distance in meters
   * @returns {number} The prediction distance in meters.
   * @private
   */
  private predictDistanceFromSegmentDistance(segmentDistance: number): number{
    switch (true) {
      case segmentDistance >= 2500: return 750;
      case segmentDistance >= 1000: return 400;
      case segmentDistance >=  500: return 225;
      default:                      return   0;
    }
  }
  //endregion Predictions

  /**
   * Returns a localized string indicating the distance left before the next instruction. Defaults to english
   * @example
   * "Dans 150m, tournez à droite"
   * "In 150m, turn right"
   * "A 150m, girar a la derecha"
   * @param distanceLeft
   * @private
   */
  private getLocalizedDistanceLeftText(distanceLeft: number){
    //TODO Create new key in dictionary instead of this...
    let unit = distanceLeft >= 1000 ? "km" : "m";
    let distance = unit == "km" ? distanceLeft / 1000 : distanceLeft;
    switch(this.translate.currentLang){
      case 'fr': return `Dans ${distance} ${unit}, `;
      case 'es': return `A ${distance} ${unit}, `;
      default: return `In ${distance} ${unit}, `;
    }
  }

  /**
   * Computes the average speed from previous GPS events (from the current segment only), within a specified time window.
   * If the time window is null, includes all available events from the current segment.
   *
   * @param {number | null } seconds - The time window (in seconds) to look back from now. All GPS events of the segment are used if null.
   * @returns {number} The average speed of all matching events, 0 if no events are found.
   */
  private recentAvgSpeed(seconds: number | null = null): number{
    let recentEvents: { speed: number; timestamp: number }[];
    let timeWindow = Date.now() - ((seconds ?? 0) * 1000);
    recentEvents = seconds ? this.speedsSinceLastInstruction.filter((event: { speed: number; timestamp: number }) => event.timestamp > timeWindow) : this.speedsSinceLastInstruction;

    if (recentEvents.length === 0)
      return 0;

    return recentEvents.reduce((sum, e) => sum + e.speed, 0) / recentEvents.length;
  }

  /**
   * Called when the RoutingResultEvent event is raised
   * Setups the routing for navigation
   * @param e The L.Routing.RoutingResultEvent object
   * @private
   */
  private routeFoundHandler(e: L.Routing.RoutingResultEvent){
    let r: L.Routing.IRoute = e.routes[0] as any;

    // Clear previous turn markers before possibly adding new ones
    for(let i = 0; i < this.turnMarkers.length; i++){
      this.map.removeLayer(this.turnMarkers[i]);
    }
    this.turnMarkers.length = 0;

    const turnPoints: L.LatLng[] = this.findTurnPoints(r, true);
    this.route = {
      instructions: this.setInstructions(r, turnPoints),
      waypoints: r.waypoints as number[] | L.LatLng[],
      coordinates: r.coordinates as L.LatLng[],
      name: r.name as string,
      summary: r.summary as L.Routing.IRouteSummary,

      // Custom properties
      turnPoints: turnPoints,
    };

    // Zoom to bbox
    this.map.flyToBounds(L.latLngBounds(this.route.coordinates), { noMoveStart: true, animate: true, easeLinearity: 0.25, duration: 1.5, padding: [25, 25] });
    this.isZoomLocked = true;

    // Add button if not already there
    this.startNavControl.remove();
    this.startNavControl.addTo(this.map);
  }

  /**
   * Called when the 'Start navigation' button is clicked by the user
   * @private
   */
  private startNavigationHandler(){
    // Hide the 'Start navigation' button by removing the control from the map
    this.startNavControl.remove();
    this.navigationStarted = true;
    this.isZoomLocked = false;

    // Re-focus on current position
    this.currentZoomLevel = 18;
    this.map.flyTo(this.userMarker.getLatLng(), this.currentZoomLevel, { noMoveStart: true, animate: true, easeLinearity: 0.25, duration: 1.5 });

    if (this.route) {
      console.log('Starting navigation!');

      // Start the index after the start instruction
      this.instructionIndex = 1;

      // Read the start instruction
      this.readText(this.route.instructions[0]?.text ?? "");

      // Reset recorded speeds
      this.speedsSinceLastInstruction = [];

      this.distanceLeftToNextSegment = Number.MAX_VALUE;

      // Check if there is an instruction following the start instruction
      if(this.instructionIndex < this.route.instructions.length){
        // Fetch following instruction
        const nextInstruction = this.route.instructions[this.instructionIndex];
        if(nextInstruction){
          // Check how much distance left to reach next instruction
          // const nextDistanceLeft = this.userMarker.getLatLng().distanceTo(nextInstruction.turnCoordinates);
          const nextDistanceLeft = this.userMarker.getLatLng().distanceTo(nextInstruction.segmentStartCoordinates);

          // Round down (floor)
          const nextRoundedDistanceLeft = DriverRoutesComponent.roundValueBasedOnDistance(nextDistanceLeft);

          // Read next instruction with distance to give a rough idea of the route to follow to the driver
          this.readText(this.getLocalizedDistanceLeftText(nextRoundedDistanceLeft) + nextInstruction.text);
        }
      }
    } else {
      alert("Error with the route");
      console.error("Invalid route object : ", this.route);
    }
  }

  /**
   * Adds extra properties to the IInstruction generated by LRM from ORS' response
   * @param r
   * @param turnPoints
   * @private
   */
  private setInstructions(r: L.Routing.IRoute, turnPoints: L.LatLng[]): IInstruction[]{
    if(!r || !r.coordinates){
      console.error(`Unable to process instructions. Invalid route or coordinates. [r:${!!r}|coordinates:${!!r?.coordinates}]`);
      return [];
    }

    let lastInstruction: IInstruction | null;
    return r.instructions?.map((instruction: any, index: number) => {
      let newInstruction: IInstruction = {
        time: instruction.time,
        distance: instruction.distance,
        text: instruction.text ?? "",

        // According to LRM's documentation, these values could be present instead of instruction.text
        type: instruction.type ?? "",
        road: instruction.road ?? "",
        direction: instruction.direction ?? "",
        exit: instruction.exit ?? 0,

        // Custom properties
        wasReadFromAfar: false,
        wasReadFromNear: false,
        // Coordinate at the start of this segment
        segmentStartCoordinates: turnPoints[index],
        //turnCoordinates: turnPoints[index],
        // Coordinate at the end of this segment
        segmentEndCoordinates: (index + 1) < (turnPoints.length) ? turnPoints[index+1] : r.coordinates![r.coordinates!.length-1],
        distanceFromLastInstruction: lastInstruction != null ? lastInstruction.distance : 0, // Custom field
      };
      lastInstruction = instruction;
      return newInstruction;
    }) ?? [];
  }

  /**
   * Finds the coordinates of every turn within the given route
   * Optionally adds a marker to the map
   * @param r The L.Routing.IRoute object from which to find the turn points
   * @param addToMap Adds a marker to the map. Useful for debugging
   * @private
   */
  private findTurnPoints(r: L.Routing.IRoute, addToMap = false): L.LatLng[]{
    if(!r || !r.coordinates){
      console.error(`Unable to process turn points. Invalid route or coordinates. [r:${!!r}|coordinates:${!!r?.coordinates}]`);
      return [];
    }

    let turnPointCoordinates = [];
    let distanceFromStart = 0;

    // The first 'turn' is actually the route's starting point
    turnPointCoordinates.push(r.coordinates[0]);
    // -1 is needed since the last 'turn' is actually the arrival
    for(let i = 0; i < (r?.instructions?.length ?? 0) - 1; i++){
      distanceFromStart += r.instructions?.[i]?.distance ?? 0;
      let coords: L.LatLng = DriverRoutesComponent.findClosestTurnPointCoordinate(distanceFromStart, r.coordinates ?? []);
      turnPointCoordinates.push(coords);

      if(addToMap){
        const turnMarker = L.marker(coords, {
          icon: new L.DivIcon({
            html: '<div style="width: 10px; height: 10px;background-color: #5ad21e;border-radius: 50%;position: absolute;top: 50%;left: 50%;transform: translate(-50%, -50%);"></div>',
          })});
        this.turnMarkers.push(turnMarker);
        this.map.addLayer(turnMarker);
      }
    }

    return turnPointCoordinates;
  }

  /**
   * Finds the closest coordinate in the route to the given distance, within some margin
   * @param targetDistance In meters
   * @param coordinates L.LatLng array of the route's coordinates
   * @param margin Margin used when testing coordinates. In meters, defaults to 2
   * @private
   */
  private static findClosestTurnPointCoordinate(targetDistance: number, coordinates: L.LatLng[], margin: number = 2): L.LatLng {
    // Iterate through all the coordinates of the route and add up the distance between them
    // (distance between coords 0 and 1, between coords 1 and 2, between coords 2 and 3, etc.)
    // Then, in each iteration, check if the calculated sum matches the target distance
    // If it does, we found the coordinate matching with the instruction's location
    // In other words, where on the route we need to turn
    let runningDistance = 0;
    for (let i = 1; i < coordinates.length; i++) {
      runningDistance += coordinates[i - 1].distanceTo(coordinates[i]); // Add to total traveled distance

      // Does current calculated distance match target distance?
      if (Math.abs(runningDistance - targetDistance) <= margin) {
        return coordinates[i];
      }
    }

    // Fallback to last coordinates (needed for 'destination reached' instruction)
    return coordinates[coordinates.length - 1];
  }

  /**
   * Check if we have reached the destination's position
   * <br>
   * Considers the destination to be reached if we are withing this.binProximityThreshold of it.
   * @param destination
   * @private
   */
  private checkIfAtDestination(destination: L.LatLng){
    const userPosition = this.userMarker.getLatLng();
    const distance = userPosition.distanceTo(destination); // Distance in meters
    const button = document.getElementById('pickupBinButton');
    // Check if the distance to the destination is within a threshold (e.g., 50 meters)
    if (distance <= this.binProximityThreshold) {
      button?.removeAttribute('disabled');

      // Once we reached, end navigation
      this.navigationStarted = false;
    }
    else{
      button?.setAttribute('disabled', 'true');
    }
  }

  /**
   * Function that use the Web Speech API to read a text on the device
   * @param text text to read out loud
   * @see readTexts
   */
  private readText(text: string) {
    // Mute check
    if(!this.isSoundMuted){
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = this.langToUseWithVoice;
      const selectedVoice = this.voices.find(voice => voice.lang === this.langToUseWithVoice);
      if (selectedVoice) {
        utterance.voice = selectedVoice; // Assign the selected voice
      } else {
        console.warn('Voice not found, using default voice.');
      }
      window.speechSynthesis.speak(utterance);
    }
  }

  /**
   * Same as readText but takes in a array of texts to be read out loud through TTS
   * @param texts Array of the texts to read out loud
   * @see readText
   */
  private readTexts(texts: string[]){
    if(texts.length > 0){
      // Cancel previous TTS to prevent unwanted queuing of instructions (in case previous instructions are still being read)
      //this.stopVoice();
      texts.forEach(text => {
        this.readText(text);
      });
    }
  }

  /**
   * Function that handles and logs geolocation error
   * @param error
   */
  private GeolocationPositionErrorHandling(error: L.ErrorEvent|GeolocationPositionError){
    switch (error.code) {
      case GeolocationPositionError.PERMISSION_DENIED:
        alert("User denied the request for Geolocation.");
        break;
      case GeolocationPositionError.POSITION_UNAVAILABLE:
        alert("Location information is unavailable.");
        break;
      case GeolocationPositionError.TIMEOUT:
        alert("The request to get user location timed out.");
        break;
      default:
        alert("An unexpected error occurred.");
        break;
    }
  }

  /**
   * Cancels any ongoing TTS
   * Triggered programmatically and from button
   */
  public stopVoice(){
    window.speechSynthesis.cancel();
  }

  /**
   * Triggered when user clicks on the volume button
   */
  volumeButton(){
    let speaker_icon = document.getElementById('speaker-icon')
    // null check
    if(speaker_icon){
      // if volume is un-muted
      if(speaker_icon.classList.contains('bi-volume-up')){
        // Change volume bootstrap icons
        speaker_icon.classList.remove('bi-volume-up');
        speaker_icon.classList.add('bi-volume-mute');
        this.isSoundMuted = true;
        // Cancel TTS
        this.stopVoice();
      }
      // If volume is muted
      else if(speaker_icon.classList.contains('bi-volume-mute')){
        // Change volume bootstrap icons
        speaker_icon.classList.add('bi-volume-up');
        speaker_icon.classList.remove('bi-volume-mute');
        this.isSoundMuted = false;
      }
    }
  }

  async returnToRoutes(){
    // Stop the TTS if it's playing
    this.stopVoice();
    const message: string = await firstValueFrom(this.translate.get('stillHaveRoutes'));
    let shouldBreak: boolean = false; // bool to check if we broke the loop
    // Loop through our stop array
    for (const stop of this.routeService.stopsArray) {
      // Do we have stops that are still in progress?
      if (stop.status === 'I') {
        shouldBreak = true;
        break;
      }
    }
    // If we broke the loop, it means we still have stops to complete.
    if(shouldBreak){
      // Display "still have stop to complete" message and on confirm change HTML
      if(confirm(message)) {
        // TODO add log, user quit his routes before completing it
        this.wakeLockService.releaseWakeLock();
        this.switchHTML();
        this.route_id = '';
        this.selectedStopDetails = undefined;
        this.selectedStopIndex = undefined;
      }
    }
    // Else we have completed the route
    else{
      this.wakeLockService.releaseWakeLock();
      this.switchHTML();
      this.route_id = '';
    }
  }

  //region Pickup modal
  openPickupModal(){
    // Null check
    if(this.pickupModal){
      // Is our modal already visible?
      if(this.pickupModal.style.visibility != 'visible'){
        // Set element visibility
        this.pickupModal.style.visibility = 'visible';
      }
    }
  }
  closePickupModal(){
    if(this.pickupModal){
      // Set element visibility
      this.pickupModal.style.visibility = 'hidden'
    }
  }
  async acceptButton() {
    // Null check
    if(this.selectedStopDetails){
      // Get our bin_id
      const stop_id = this.selectedStopDetails.route_stop_id;
      // Make the Service call and return true on success, or false
      const success = await this.routeService.updateRouteStopHTTP(stop_id, 'C');
      if(success){
        // Change the status of the stop in the stopsArray to Complete
        this.changeStatusOfStopInArray('C');
        // Put new css classes on success
        this.disableStopLabelOnSuccess();
        // Close modal
        this.closePickupModal();
        // Log the stop report
        this.routeService.logRouteStop(stop_id, this.driver_id, 'C')
        // Reset our selectedStop, do this last
        this.selectedStopDetails = undefined;
        this.checkRouteCompletion();
      }
      else{
        alert('Something went wrong while making the request, pls try again');
      }
    }
  }
  cantButton() {
    this.closePickupModal();
    this.openCantPickupModal();
  }
  //endregion Pickup modal

  //region Cant pick up modal
  // Open or close the cant pickup modal
  openCantPickupModal(){
    // Null check
    if(this.cantPickupModal){
      // Hide or display element
      if(this.cantPickupModal.style.visibility == 'visible'){
        this.cantPickupModal.style.visibility = 'hidden';
      }else{
        this.cantPickupModal.style.visibility = 'visible';
      }
    }
  }
  // Close the cant pickup modal
  closeCantPickupButton() {
    // Null check
    if(this.cantPickupModal){
      // Hide element
      this.cantPickupModal.style.visibility = 'hidden';
      this.selectedError = '';
      this.cantPickupTextDetail = '';
    }
  }
  // Accept the cant pick up error
  async acceptCantPickupButton(){
    // Check if for the select validation
    this.checkValidationOfPickupError();
    if(this.validationService.pickupErrorSelectValid){
      // Null check
      if(this.selectedStopDetails){
        const stop_id = this.selectedStopDetails.route_stop_id;
        // Update stop status with 'P' for pending so the route job can reschedule it
        const success = await this.routeService.updateRouteStopHTTP(stop_id,'P')
        if(success){
          // Log the error
          this.routeService.logRouteStop(stop_id, this.driver_id, 'X', this.selectedError, this.cantPickupTextDetail);
          // Change the status of the stop in the Array to match the DB
          this.changeStatusOfStopInArray('P');
          // Close the cantPickupModal
          this.closeCantPickupButton();
          // Add error css
          this.addStopLabelErrorCss();
          this.selectedStopDetails = undefined
          this.checkRouteCompletion();
        }else{
          // TODO make proper error handling and log in fail attempt
          alert('Something went wrong while making the request, pls try again');
        }
      }
    }
  }
  //endregion Cant pick up modal

  private addStopLabelErrorCss() {
    if(this.selectedStopDetails){
      const elements = document.querySelectorAll<HTMLOptionElement>('.stop-label');
      const index = this.selectedStopDetails.index;
      const element = elements[index - 1];
      element.classList.add('stop-label-error');
      element.disabled = true;
    }else{
      console.error('The selected stop is undefined.')
    }
  }

  /**
   *  Change the status of our stop in its array, the new status. MUST BE A SINGLE CHARACTER.
   *  Check the ../constants/route-stop-status.ts for all the possible status.
   * */
  private changeStatusOfStopInArray(newStatus:string) {
    // Loop through our stops Array
    for (const stop of this.routeService.stopsArray) {
      // When we find a matching bin_id, change the status of the stop
      if (stop.bin_id == this.selectedStopDetails?.bin_id) {
        stop.status = newStatus;
        break;
      }
    }
  }

  private disableStopLabelOnSuccess(){
    if(this.selectedStopDetails){
      // Get all our stop labels
      const stop_labels = document.querySelectorAll('.stop-label');
      // Get our selected stop index
      const stop_index = this.selectedStopDetails?.index;
      // HTML element we want to add css to
      const elem = stop_labels[stop_index-1];
      elem.classList.add('stop-label-success', 'disabled');
    }else{
      console.log('selectedStop is undefined');
    }
  }

  checkValidationOfPickupError() {
    this.validationService.validatePickupErrorSelect(this.selectedError);
    // Define a mapping of error codes to corresponding error messages
    const errorMappings: Record<string, string> = {
      'PickupErrorSelectInvalid': 'pickupErrorSelectInvalid'
    };
    // Retrieve the first validation error from the array
    const validationError = this.validationService.validationErrorArray[0];
    // Check if the validation error code exists in the mapping
    if (errorMappings[validationError]) {
      this.systemMessageService.buttonBlocked = true;
      // If so, display a danger ribbon message with the corresponding key
      this.systemMessageService.selectRibbon('danger', errorMappings[validationError]);
    }
  }

  /**
   * Loop through this.routeService.stopsArray to see if we still have stop in progress. If none are left display
   * completion and return to the routes page.
   */
  async checkRouteCompletion(){
    // Check if we still have stop in progress
    const routeComplete = !this.routeService.stopsArray.some(stop => stop.status === 'I');
    if(routeComplete){
      this.closePickupModal();
      this.routeService.endRoute(this.route_id);
      let route = this.routeService.driverRoutesArray.find((route:any) => route.route_id == this.route_id);
      // set end_date so the route tile become disabled when we return to the routes page
      route.end_date = Math.floor(Date.now() / 1000);
      this.switchHTML();
      const message = await firstValueFrom(this.translate.get('routeCompleted'));
      alert(message);
    }
  }

  /**
   * Sets the TTS language based on the TranslateService's currently set language
   * @private
   */
  private setLanguageToUseForTTS(){
    const lang = this.translate.currentLang;
    switch(lang){
      case 'fr':
        this.langToUseWithVoice = 'fr-FR'
        break;
      case 'en':
        this.langToUseWithVoice = 'en-US'
        break;
      case 'es':
        this.langToUseWithVoice = 'es-ES'
        break;
    }
  }

  /**
   * Helper function to check if the position has changed significantly,
   * takes a new position and compares it with our last userMarker position,
   * returns true or false depending if we reach our distance threshold.
   * @param oldPosition {L.LatLng}
   * @param newPosition {L.LatLng}
   * @returns bool
   */
  private hasPositionChanged(oldPosition: L.LatLng, newPosition: L.LatLng): boolean {
    // Only consider the position as changed if the distance is greater than a threshold (e.g., 10 meters)
    // This helps avoid false updates due to poor/unstable GPS accuracy
    return oldPosition.distanceTo(newPosition) >= this.positionUpdateThreshold;
  }

  /**
   * Triggered when the user attempts to pickup a bin from the UI
   */
  public pickupBinButton(){
    if(this.isInProximityAndInProgress(this.selectedStopIndex)){
      this.openPickupModal();
    }
  }

  /**
   * Returns an instance of L.Icon with the iconUrl property set to
   * <pre>
   *   class='bi bi-${iconName} ${size} ${(useShadow ? 'shadow-lg' : '')}'
   * </pre>
   * <br>
   * Adjust the color with any valid HTML color (color name, hex code, etc.)
   * <br>
   * Adjust the size with standard bootstrap sizes (fs-1, fs-2, etc.)
   * @see https://icons.getbootstrap.com/
   * @param iconName The bootstrap icon name
   * @param color The color for the icon
   * @param size The icon's size
   * @param useShadow Whether to display a shadow or not. Usually helps with contrast
   * @return {L.DivIcon} The icon to be used by Leaflet
   */
  public createStandardMarkerIcon(iconName: string, color: string = "#666", size: string = "fs-3", useShadow: boolean = true): L.DivIcon{
    return new L.DivIcon({
      html: `<i class='bi bi-${iconName} ${size} ${(useShadow ? 'shadow-lg' : '')}' style='color:${color}'></i>`,
      iconSize: [30, 30],   // Size of the icon
      iconAnchor: [15, 30], // Anchor point of the icon
      //shadowSize: [41, 41], // Shadow size
    });
  }

  /**
   * Sets this.setUserInteractedWithMap as the handler for events related to the user interacting with the map (drag and zooming the map)
   */
  private registerUserMapInteractionHandlers(): void {
    // drag events are always user-initiated
    this.map?.on('drag', this.setUserInteractedWithMap.bind(this));

    // Zoom event can be both programmatic or user-initiated
    this.map?.on('zoomstart', (e: any) => {
      // if this.isZoomLocked is false, means the zoom was NOT triggered by our code. Most likely caused by the user
      if(!this.isZoomLocked){
        this.setUserInteractedWithMap.bind(this)();
        console.warn('movestart stack:', new Error().stack);
      }
    });
  }

  /**
   * Sets this.userInteractedWithMap to true for a delay of this.mapAutoFocusTimeThreshold
   * After the delay, reset it back to false
   * If a delay is already in place, it is removed and replaced (avoids duplicated triggers)
   */
  private setUserInteractedWithMap(): void {
    this.userInteractedWithMap = true;

    // Clear timeout if it already exists
    if (this.resetInteractionTimeoutId !== -1) {
      window.clearTimeout(this.resetInteractionTimeoutId);
    }

    // Define timeout to unlock map after a predefined delay
    this.resetInteractionTimeoutId = window.setTimeout(() => {
      this.moveMapSmoothly(this.userMarker.getLatLng(), this.currentZoomLevel, true, 1.5, () => this.userInteractedWithMap = false);
    }, this.mapAutoFocusTimeThreshold);
  }

  /**
   * Performs a smooth pan/zoom to the given targetLocation and targetZoom. In case of a zoom change, locks the zoom for the duration
   * <br>
   * While the zoom is locked, every call to this function will immediately return. This is to avoid breaking the smooth flyTo animation used in zooms
   * @param targetLocation {L.LatLng} The target location
   * @param targetZoom {number} The target zoom level
   * @param forceFlyTo {boolean} Forces the use of flyTo
   * @param duration {number} The duration (in sec) for the zoom animation. Only used if zoom is different from the map's current zoom, or if forceFlyTo is set to true (default 1.5sec)
   * @param callback {() => void} This callback is called after the flyTo animation is over (**NOT PRECISE**)
   * @private
   */
  private moveMapSmoothly(targetLocation: L.LatLng, targetZoom: number, forceFlyTo: boolean = false, duration: number = 1.5, callback: (() => void) | undefined = undefined): void {
    if (this.isZoomLocked)
      return;

    // Only perform a zoom animation if zoom has changed
    if (forceFlyTo || targetZoom != this.map.getZoom()) {
      // Lock zoom
      this.isZoomLocked = true;
      // Zoom using flyTo
      this.map.flyTo(targetLocation, targetZoom, { noMoveStart: true, animate: true, duration: duration });

      // Give the flyTo animation enough time to complete
      window.setTimeout(() => {
        if(callback && typeof callback === "function")
          callback();

        // Unlock zoom after animation is over
        this.isZoomLocked = false;
      }, duration * 1000 + 100); // 100ms buffer time, extra safety
    }
    else {
      // We can safely use animate: true here to smooth out updates even because panTo's animation will self-reset on every call (flyTo would cause issues though)
      this.map.panTo(targetLocation, { noMoveStart: true, animate: true });
    }
  }
}
